import { useEffect, useState } from "react";
import { ChefHat, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", description: "", price_per_head: "", items_text: "" };

const parseItems = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [name, course, portion, allergens] = l.split("|").map((s) => (s || "").trim());
  return { name, course: course || "main", portion: portion || "1 pp", allergens: allergens ? allergens.split(";").map((a) => a.trim()).filter(Boolean) : [] };
});

export default function Menus() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/menus").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, description: m.description || "", price_per_head: m.price_per_head, cost_per_head: m.cost_per_head || "", items_text: (m.items || []).map((it) => `${it.name} | ${it.course} | ${it.portion}${it.allergens?.length ? ` | ${it.allergens.join("; ")}` : ""}`).join("\n") });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { name: form.name, description: form.description, price_per_head: Number(form.price_per_head), cost_per_head: Number(form.cost_per_head || 0), items: parseItems(form.items_text) };
    try {
      if (editing) await api.patch(`/menus/${editing.id}`, payload);
      else await api.post("/menus", payload);
      toast.success(editing ? "Menu updated" : "Menu created");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete menu "${m.name}" permanently?`)) return;
    try {
      await api.delete(`/menus/${m.id}`);
      toast.success("Menu deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleLive = async (m) => {
    try {
      await api.patch(`/menus/${m.id}`, { archived: !m.archived });
      toast.success(m.archived ? `${m.name} is live` : `${m.name} switched off`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="menus-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Menus</h1>
          <p className="mt-1 text-sm text-muted-foreground">Menu packages with per-head pricing — drive quotes and kitchen production sheets. Off menus are hidden from your public hub.</p>
        </div>
        <Button data-testid="new-menu-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> New Menu
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((m) => (
          <div key={m.id} data-testid={`menu-card-${m.id}`} className={`border border-border bg-white p-5 transition-transform duration-200 hover:-translate-y-[2px] ${m.archived ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-muted"><ChefHat className="h-5 w-5 text-ink" /></div>
                <div>
                  <h3 className="font-heading text-base font-bold tracking-tight">{m.name}</h3>
                  <p className="text-xs text-muted-foreground">{m.description}</p>
                </div>
              </div>
              <span className="font-heading text-lg font-black text-brand tabular-nums" data-testid={`menu-price-${m.id}`}>{fmt(m.price_per_head)}<span className="text-[10px] font-medium text-muted-foreground">/head</span></span>
            </div>
            <div className="mt-4 space-y-1.5 border-t border-border pt-4">
              {(m.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span>{it.name}</span>
                  <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{it.course} · {it.portion}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                <Switch data-testid={`menu-toggle-${m.id}`} checked={!m.archived} onCheckedChange={() => toggleLive(m)} />
                {m.archived ? "Off" : "Live"}
              </label>
              <div className="flex gap-2">
                <Button data-testid={`edit-menu-${m.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openEdit(m)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button data-testid={`delete-menu-${m.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => remove(m)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-3 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No menus yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="menu-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Menu" : "New Menu"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="menu-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Input data-testid="menu-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Price per head (£)</Label><Input data-testid="menu-price-input" type="number" step="0.5" min="1" required value={form.price_per_head} onChange={(e) => setForm({ ...form, price_per_head: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cost per head (£)</Label><Input data-testid="menu-cost-input" type="number" step="0.5" min="0" placeholder="For GP tracking" value={form.cost_per_head} onChange={(e) => setForm({ ...form, cost_per_head: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Items — one per line: Dish | course | portion | allergens (semicolon separated)</Label>
              <Textarea data-testid="menu-items-input" rows={5} placeholder={"Brisket bap | main | 1 pp\nLoaded fries | side | 1 pp"} value={form.items_text} onChange={(e) => setForm({ ...form, items_text: e.target.value })} />
            </div>
            <Button data-testid="menu-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editing ? "Save Changes" : "Create Menu"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
